import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ambulance,
  Phone,
  MapPin,
  HeartPulse,
  Loader2,
  LocateFixed,
  AlertCircle,
  PhoneCall,
  PlusSquare,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useLocationStore } from "@/store/locationStore";
import LocationPickerDialog from "@/components/LocationPickerDialog";
import SpecialistCareBanner from "@/components/SpecialistCareBanner";
import MyHospitalCard from "@/components/MyHospitalCard";
import MedicalDisclaimer from "@/components/MedicalDisclaimer";

export default function Home() {
  const navigate = useNavigate();
  const name = useAuthStore((s) => s.displayName());
  const profile = useAuthStore((s) => s.customerProfile);
  const user = useAuthStore((s) => s.user);
  const [percent, setPercent] = useState(0);

  const firstName = name.split(" ")[0];
  const profileCity = profile?.basicInfo?.city || profile?.medicalInfo?.city;

  const locLabel = useLocationStore((s) => s.label);
  const detecting = useLocationStore((s) => s.detecting);
  const detect = useLocationStore((s) => s.detect);
  const displayCity = locLabel || profileCity;
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  useEffect(() => {
    const b = profile?.basicInfo || {};
    const m = profile?.medicalInfo || {};
    const fields = [b.fullName, b.dateOfBirth, b.gender, b.city, m.bloodGroup];
    const filled = fields.filter(Boolean).length;
    setPercent(Math.round((filled / fields.length) * 100));
  }, [profile]);

  // Auto-detect realtime GPS location once when no location has been set yet.
  useEffect(() => {
    if (!locLabel) detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      {/* Location row */}
      <div className="flex items-center justify-between">
        <button className="flex items-center gap-2 min-w-0" onClick={() => setShowLocationPicker(true)} data-testid="location-row">
          <div className="h-9 w-9 rounded-xl bg-accent flex items-center justify-center shrink-0">
            <MapPin className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left min-w-0">
            <p className="text-sm font-semibold truncate">
              {detecting ? "Detecting location…" : displayCity || "Set your location"}
            </p>
            <p className="text-xs text-muted-foreground truncate">Find nearby doctors, labs & ambulance</p>
          </div>
        </button>
        <button className="text-sm font-semibold text-primary flex items-center gap-1 disabled:opacity-60" onClick={() => setShowLocationPicker(true)} disabled={detecting} data-testid="change-location">
          {detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />}
          {detecting ? "Locating…" : "Change"}
        </button>
      </div>

      <LocationPickerDialog open={showLocationPicker} onOpenChange={setShowLocationPicker} />

      <div>
        <h1 className="text-2xl font-semibold">Hello, {firstName} 👋</h1>
        <p className="text-muted-foreground mt-1">How are you feeling today?</p>
      </div>

      {/* Profile score bar (moved above Emergency SOS) */}
      {profile && (
        <button onClick={() => navigate("/profile")} data-testid="profile-completion-bar" className="w-full rounded-2xl border border-border bg-card p-4 text-left">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">📊 Profile {percent}% complete</p>
            <span className="text-sm font-semibold text-primary">{percent < 100 ? "Complete →" : "View →"}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
          </div>
        </button>
      )}

      {/* Emergency SOS — big circular button */}
      <div className="flex flex-col items-center pt-4">
        <div className="relative flex items-center justify-center" style={{ height: 150 }}>
          <span className="sos-halo-ring" />
          <span className="sos-halo-ring" />
          <span className="sos-halo-ring" />
          <button
            onClick={() => navigate("/emergency")}
            data-testid="sos-button"
            className="sos-circle-btn relative flex flex-col items-center justify-center rounded-full text-white active:scale-95 transition-transform"
            style={{ height: 132, width: 132 }}
          >
            <span
              className="relative z-10 flex flex-col items-center"
              style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.35))" }}
            >
              <PhoneCall className="h-8 w-8" strokeWidth={2.2} />
              <span className="mt-0.5 text-[11px] font-extrabold tracking-[0.15em]">EMERGENCY</span>
              <span className="text-2xl font-black leading-none tracking-wide">SOS</span>
            </span>
          </button>
        </div>

        {/* Free service capsule */}
        <div
          data-testid="sos-free-badge"
          className="sos-free-pulse mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold"
          style={{ backgroundColor: "#FDE047", color: "#7C2D12" }}
        >
          <span>⚡</span> Free
        </div>

        {/* 3 feature items */}
        <div className="mt-3 grid w-full grid-cols-3 gap-2">
          {[
            { Icon: Phone, label: "Our team responds instantly" },
            { Icon: MapPin, label: "Your location is shared" },
            { Icon: PlusSquare, label: "You get connected to the nearest help" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col items-center px-1 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: "#FEE2E2" }}>
                <f.Icon className="h-4 w-4" style={{ color: "#DC2626" }} />
              </div>
              <p className="mt-1.5 text-xs leading-tight text-muted-foreground">{f.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Life-threatening guidance banner */}
      <div
        data-testid="sos-guidance-banner"
        className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3"
      >
        <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-destructive">Use Emergency SOS for any life-threatening situation</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            e.g. heart attack, accident, severe breathing difficulty, unconsciousness, heavy bleeding, stroke, etc.
          </p>
        </div>
      </div>

      {/* Quick Call 112 (moved above Other Emergency Services) */}
      <a href="tel:112" data-testid="quick-call-112" className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 hover:bg-accent transition-colors">
        <div className="flex h-9 w-9 items-center justify-center rounded-full shrink-0" style={{ backgroundColor: "#FEE2E2" }}>
          <Phone className="h-4 w-4" style={{ color: "#DC2626" }} />
        </div>
        <div>
          <p className="text-sm font-semibold">Quick Call 112</p>
          <p className="text-xs text-muted-foreground">National emergency helpline</p>
        </div>
      </a>

      {/* Other Emergency Services */}
      <section>
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-destructive" />
            <h2 className="text-base font-semibold">Other Emergency Services</h2>
          </div>
          <p className="text-xs text-muted-foreground ml-4">Choose a specific service if you know what you need</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => navigate("/ambulance")} data-testid="critical-ambulance" className="relative rounded-3xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#FEE2E2" }}>
              <Ambulance className="h-6 w-6" style={{ color: "#DC2626" }} />
            </div>
            <p className="mt-3 font-semibold">Ambulance</p>
            <p className="text-xs text-muted-foreground">Emergency transport</p>
          </button>
          <button onClick={() => navigate("/paramedic")} data-testid="critical-paramedic" className="relative rounded-3xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#FFF3E0" }}>
              <HeartPulse className="h-6 w-6" style={{ color: "#FF6F00" }} />
            </div>
            <p className="mt-3 font-semibold">Paramedic</p>
            <p className="text-xs text-muted-foreground">Medical help at home</p>
          </button>
        </div>
      </section>

      {/* My Healthcare Network — shown above Specialist Care Services */}
      <MyHospitalCard userId={user?._id} />

      {/* Specialist Care Services — impactful banner below My Healthcare Network */}
      <SpecialistCareBanner />

      {/* Medical Disclaimer (collapsible) + Terms/Privacy */}
      <MedicalDisclaimer />
    </div>
  );
}
